import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getEnvironments,
  createEnvironments,
  updateEnvironments,
  deleteEnvironment,
  importEnvironment,
  exportEnvironment,
  deleteCacheApplication,
} from "../services/console/environment";
import { AppContext } from "../context";
import { getExportInfo } from "../services/console/environment";

type UseType = "DATA" | "ID" | "ACTIONS" | "EXPORTINFO";
type ActionType =
  | "CREATE"
  | "UPDATE"
  | "DELETE"
  | "CLONE"
  | "IMPORT"
  | "EXPORT"
  | "DELETE-CACHE";

const handleEnvActionFunction = (action: ActionType, t: Function) => {
  switch (action) {
    case "CREATE":
      return {
        actionFn: createEnvironments,
        fnMsg: t("Environments.apiCreateLabel"),
      };
    case "UPDATE":
      return {
        actionFn: updateEnvironments,
        fnMsg: t("Environments.apiUpdateLabel"),
      };
    case "DELETE":
      return {
        actionFn: deleteEnvironment,
        fnMsg: t("Environments.apiDeleteLabel"),
      };
    case "IMPORT":
      return {
        actionFn: importEnvironment,
        fnMsg: t("Applications.apiImportLabel"),
      };
    case "EXPORT":
      return {
        actionFn: exportEnvironment,
        fnMsg: t("Applications.apiExportLabel"),
      };
    case "DELETE-CACHE":
      return {
        actionFn: deleteCacheApplication,
        fnMsg: t("Applications.apiDeleteCacheLabel"),
      };
    default:
      return {
        actionFn: () => { },
        fnMsg: "",
      };
  }
};

export const useEnvironments = (type: UseType) => {
  const {
    t,
    toast,
    appState: { selectedApplication, selectedEnvironment },
    setAppState,
  } = useContext(AppContext);
  const [data, setData] = useState<any | null>(null);
  const [reload, setReload] = useState<number>(0);
  const appid = selectedApplication?.id;
  const envid = selectedEnvironment?.id;
  const navigate = useNavigate();

  const reloadFn = () => {
    setData(null);
    setReload((prev) => prev + 1);
  };

  const envAction = async (action: ActionType, data: any) => {
    const { actionFn, fnMsg } = handleEnvActionFunction(action, t);
    setAppState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    const actionStatus = await Promise.resolve(actionFn(data))
      .then(async (response) => {
        if (['CREATE', 'UPDATE', 'DELETE', 'EXPORT', 'DELETE-CACHE'].includes(action)) {
          toast.current.replace({
            severity: "success",
            summary: "",
            detail: `${fnMsg.replace('{name}', `${data[0]?.name}`)}`,
            life: 3000,
          });
          return "SUCCESS";
        };

        //Logic for show error report of import
        let errorImportConfigs: {} = {};
        let processedConfigsCounter: number = 0;
        let errorConfigsCounter: number = 0;
        Object.entries(response?.data)?.forEach(([key, value]: any) => {
          if (Array.isArray(value)) {
            processedConfigsCounter = processedConfigsCounter + value?.length;
            const valuesWithError = value.filter((elem: any) => elem?.statusImport === 'ERROR');
            errorConfigsCounter = errorConfigsCounter + valuesWithError.length;
            errorImportConfigs = { ...errorImportConfigs, [key]: valuesWithError }
          } else {
            Object.entries(value)?.forEach(([keyChild, childValue]: any) => {
              processedConfigsCounter = processedConfigsCounter + childValue?.length;
              const valuesWithError = childValue.filter((elem: any) => elem?.statusImport === 'ERROR');
              errorConfigsCounter = errorConfigsCounter + valuesWithError.length;
              errorImportConfigs = { ...errorImportConfigs, [key]: {[keyChild]:valuesWithError} }
            })
          }
        });
        if (errorConfigsCounter > 0) {
          setAppState((prev) => ({
            ...prev,
            errorConfigsList: errorImportConfigs,
            configsImportProcessed: processedConfigsCounter,
          }));
          sessionStorage.removeItem('import-config-filters-table');
          navigate('/applications/edit/import/errorimport');
          return "ERROR";
        }
        toast.current.replace({
          severity: "success",
          summary: "",
          detail: `${fnMsg.replace('{name}', `${data[0]?.name}`)}`,
          life: 3000,
        });
        return "SUCCESS";

      }
      ).catch((error) => {
        console.log(error)
        toast.current.replace({
          severity: "error",
          summary: "",
          detail: error?.response?.data?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
          life: 3000,
        });
        return "ERROR";
      });
    setAppState((prev) => ({
      ...prev,
      isLoading: false,
    }));
    return actionStatus;
  };

  useEffect(() => {
    if (type === "DATA") {
      (async function () {
        await Promise.resolve(getEnvironments(appid))
          .then((response) => {
            setData(response?.data);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [appid, toast, reload, type]);

  useEffect(() => {
    if (type === "EXPORTINFO") {
      (async function () {
        await Promise.resolve(getExportInfo(envid || ""))
          .then((response) => {
            setData(response?.data);
          })
          .catch((error) => {
            toast.current.replace({
              severity: "error",
              summary: "",
              detail: error?.response?.header?.errorMsg ?? t('ConsoleTranslations.generalErrorApiMessage'),
              life: 3000,
            });
          });
      })();
    }
  }, [envid, toast, reload, type]);

  return {
    data,
    envAction,
    reloadFn,
  };
};
